import React, { ChangeEvent, useEffect, useRef, useState } from 'react'
import IconButton from './icon-button'

export type TextInputType = 'text' | 'email' | 'password' | 'date' | 'number' | 'url'

type TextInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix' | 'value'> & {
  value?: string | number | JSX.Element
  prefix?: string | JSX.Element
  postfix?: string | JSX.Element
  clearable?: boolean
  onClear?: CallableFunction
  error?: boolean
  noWrap?: boolean
  inputClassName?: string
  size?: 'default' | 'big'
  cursor?: 'text' | 'pointer'
  fullWidth?: boolean
}
function getPostClass(hideClearButton: boolean, postfix?: string | JSX.Element, clearable?: boolean) {
  if (!postfix && !clearable) {
    return ''
  }

  let className = 'input'
  if (postfix) {
    className += '-postfix'
  }
  if (clearable && !hideClearButton) {
    className += '-clearable'
  }
  return className
}
const TextInput = ({
  prefix,
  postfix,
  onClear,
  error,
  clearable,
  inputClassName,
  noWrap,
  id,
  className = '',
  style,
  size = 'default',
  value,
  placeholder,
  autoComplete,
  onChange,
  cursor = 'text',
  fullWidth = true,
  ...inputProps
}: TextInputProps): JSX.Element => {
  const [hideClearButton, setHideClearButton] = useState(true)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setHideClearButton(!value)
  }, [value])

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(event)
    }
    setHideClearButton(event.target.value === '')
  }

  const handleClearClick = () => {
    if (inputRef.current) {
      inputRef.current.value = ''
      // Use input's id and empty value
      const changeEvent = {
        currentTarget: { ...inputRef.current, id: id, value: '' },
        target: { value: '' },
      } as ChangeEvent<HTMLInputElement>

      if (onChange) {
        onChange(changeEvent)
      }
      if (onClear) {
        onClear()
      }
      setHideClearButton(true)
    }
  }

  const handleFocusInput = () => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }

  className = `input-field input-field-${size} ${className} ${error ? 'input-field-error' : ''} ${
    noWrap ? 'no-wrap' : ''
  } ${inputProps.disabled ? 'disabled' : ''}`

  const postClass = getPostClass(hideClearButton, postfix, clearable)

  // A plain wrapper, not a <button>: an interactive element nesting an <input> is invalid markup
  // and breaks keyboard and screen-reader behaviour. The click handler only forwards focus to the input.
  return (
    <div
      className={`${className} d-inline-block ${fullWidth ? 'w-100' : ''}`}
      style={{ ...style, cursor }}
      onClick={handleFocusInput}
    >
      <div className="prefix">{prefix}</div>

      {typeof value === 'string' || typeof value === 'number' || typeof value === 'undefined' || value === null ? (
        <input
          id={id}
          ref={inputRef}
          className={`${inputClassName ?? ''} ${prefix ? 'input-prefix' : ''} ${postClass}`}
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          autoComplete={autoComplete ?? (inputProps.type === 'password' ? 'off' : 'on')}
          {...inputProps}
        />
      ) : (
        <div
          id={id}
          className={`html-value-input ${inputClassName ?? ''} ${prefix ? 'input-prefix' : ''} ${postClass}`}
        >
          {value}
        </div>
      )}

      <div className="postfix">
        {clearable && !hideClearButton && (
          <IconButton iconClass="ds3-icon ds3-close ds3-hover" onClick={handleClearClick} />
        )}
        {postfix}
      </div>
    </div>
  )
}

export default TextInput
